// src/lib/lead/tags.ts
import type { ExtractedLeadV2 } from "../../types/lead";
import type { Location } from "../../types/location";

// Tag suggestions
export function buildBuddyTags(
  extracted: ExtractedLeadV2 | null,
  location: Location | null,
  version: string,
  appDisplayName?: string
): string[] {
  const tags: string[] = [];

  // Always tag app source + version for GHL auditing
  tags.push("turbo_assist");
  tags.push(`turbo_assist_v${version}`);

  // Org-specific app name tag — only when explicitly provided (e.g. "benny", "custom_app_name").
  // Not emitted when falling back to defaults so no misleading tag appears for unconfigured orgs.
  if (appDisplayName) {
    const appSlug = appDisplayName.trim().toLowerCase().replace(/\s+/g, "_");
    if (appSlug) tags.push(appSlug);
  }

  if (location?.id) {
    // e.g. "lot_moncks_corner"
    tags.push(location.id);
  }

  if (location?.name) {
    // human-friendly lot tag, e.g. "lot_city_moncks_corner"
    tags.push(`lot_city_${location.name.toLowerCase().replace(/\s+/g, "_")}`);
  }

  // GHL routing tag — bare city slug for workflow filtering (e.g. "ravenel", "moncks_corner")
  // Derived from location.id: strip "lot_" prefix. Virtual/non-lot IDs are skipped.
  if (location?.id?.startsWith("lot_")) {
    tags.push(location.id.slice(4));
  }

  if (extracted) {
    // Product line tags
    if (extracted.product_line === "Sheds") {
      tags.push("prod_sheds");
    } else if (extracted.product_line === "Steel Structures") {
      tags.push("prod_steel");
    } else if (extracted.product_line === "Other") {
      tags.push("prod_other");
    }

    // GHL routing tag — bare product slug for workflow filtering (e.g. "sheds", "steel_structures")
    if (extracted.product_line) {
      tags.push(extracted.product_line.toLowerCase().replace(/\s+/g, "_"));
    }

    // Product subtype tags, e.g. "Lofted Barn" -> "sub_lofted_barn"
    if (extracted.product_subtype) {
      const sub = extracted.product_subtype
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "_");
      if (sub) {
        tags.push(`sub_${sub}`);
      }
    }

    // Size options tags, e.g. ["10x16", "12x24"] -> ["size_10x16", "size_12x24"]
    if (Array.isArray(extracted.size_options)) {
      for (const rawSize of extracted.size_options) {
        if (typeof rawSize !== "string") continue;
        const cleaned = rawSize.trim();
        if (!cleaned) continue;
        const normalized = cleaned.replace(/\s+/g, "");
        tags.push(`size_${normalized}`);
      }
    }

    // Temperature tags
    if (extracted.interest_level === "hot") {
      tags.push("temp_hot");
    } else if (extracted.interest_level === "warm") {
      tags.push("temp_warm");
    } else if (extracted.interest_level === "cold") {
      tags.push("temp_cold");
    }

    // Lead source tags
    if (extracted.lead_source_category) {
      tags.push(`src_${extracted.lead_source_category}`);
    }

    // Timeline tags
    if (extracted.ready_to_buy_timeline) {
      tags.push(`time_${extracted.ready_to_buy_timeline}`);
    }

    // Optionally surface any LLM-generated interest_tags
    if (Array.isArray(extracted.interest_tags)) {
      for (const t of extracted.interest_tags) {
        if (typeof t === "string" && t.trim()) {
          tags.push(t.trim().toLowerCase().replace(/\s+/g, "_"));
        }
      }
    }
  }

  // De-duplicate
  return Array.from(new Set(tags));
}
